
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Hero from '@/components/Hero';
import { initPerformanceOptimizations } from '@/services/PerformanceOptimizer';

// Component loading states
const LoadingFallback = () => (
  <div className="flex justify-center items-center py-6">
    <div className="animate-pulse h-8 w-full max-w-md bg-gray-100 rounded-lg"></div>
  </div>
);

// Lazy load non-critical components with proper chunk naming
const HowItWorks = lazy(() => import(/* webpackChunkName: "how-it-works" */ '@/components/HowItWorks'));
const TripleAdvantage = lazy(() => import(/* webpackChunkName: "triple-advantage" */ '@/components/TripleAdvantage'));
const CustomerSuccessStories = lazy(() => import(/* webpackChunkName: "success-stories" */ '@/components/CustomerSuccessStories'));
const VariantTable = lazy(() => import(/* webpackChunkName: "variant-table" */ '@/components/VariantTable'));
const ATSIntegrations = lazy(() => import(/* webpackChunkName: "ats-integrations" */ '@/components/ATSIntegrations'));
const FAQ = lazy(() => import(/* webpackChunkName: "faq" */ '@/components/FAQ'));
const Footer = lazy(() => import(/* webpackChunkName: "footer" */ '@/components/MainFooter'));

// Performance measurement component
const PerformanceMetrics = () => {
  const [metrics, setMetrics] = useState({
    fcp: 0,
    lcp: 0,
    cls: 0
  });

  useEffect(() => {
    // Report Web Vitals
    if ('performance' in window && 'getEntriesByType' in performance) {
      // First Contentful Paint
      let paintEntries = performance.getEntriesByType('paint');
      let fcpEntry = paintEntries.find(entry => entry.name === 'first-contentful-paint');
      if (fcpEntry) {
        setMetrics(prev => ({ ...prev, fcp: Math.round(fcpEntry.startTime) }));
      }

      // Observe LCP
      let lcpObserver = new PerformanceObserver((entryList) => {
        let entries = entryList.getEntries();
        let lastEntry = entries[entries.length - 1];
        setMetrics(prev => ({ ...prev, lcp: Math.round(lastEntry.startTime) }));
      });
      
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

      // Observe CLS - Fix type issues with LayoutShift interface
      let clsObserver = new PerformanceObserver((entryList) => {
        let entries = entryList.getEntries();
        let clsScore = 0;
        entries.forEach(entry => {
          // Check if this is a layout shift entry with the right properties
          if (entry.entryType === 'layout-shift' && 'hadRecentInput' in entry && !entry['hadRecentInput']) {
            clsScore += (entry as any).value;
          }
        });
        setMetrics(prev => ({ ...prev, cls: Math.round(clsScore * 100) / 100 }));
      });
      
      clsObserver.observe({ type: 'layout-shift', buffered: true });

      return () => {
        lcpObserver.disconnect();
        clsObserver.disconnect();
      };
    }
  }, []);

  // Only show in development
  if (process.env.NODE_ENV !== 'development') return null;

  return (
    <div className="fixed bottom-0 left-0 bg-white/90 text-xs p-2 text-gray-700 z-50 rounded-tr-md">
      <div>FCP: {metrics.fcp}ms</div>
      <div>LCP: {metrics.lcp}ms</div>
      <div>CLS: {metrics.cls}</div>
    </div>
  );
};

const Index = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Set document title
    document.title = "Boost your talent pipeline at 3.5% fees";
    
    // Apply all performance optimizations
    initPerformanceOptimizations();
    
    // Mark page as loaded
    setIsLoaded(true);
    
    // Log performance metrics if in development
    if (window.performance && process.env.NODE_ENV === 'development') {
      setTimeout(() => {
        const perfData = window.performance.timing;
        const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
        console.log('Page load time:', pageLoadTime);
        
        // Log memory usage if available
        if (performance && 'memory' in performance) {
          console.log('Memory usage:', (performance as any).memory);
        }
      }, 3000);
    }
  }, []);

  return (
    <div 
      className={`min-h-screen ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
      style={{ contentVisibility: 'auto' }} // CSS containment for better paint performance
    >
      <Helmet>
        <title>Boost your talent pipeline at 3.5% fees | Cutshort</title>
        <meta name="description" content="Cutshort is the hiring platform that brings AI, human judgement & a strong talent pool of 3.5 million candidates." />
      </Helmet>
      {process.env.NODE_ENV === 'development' && <PerformanceMetrics />}
      
      <NavBar />
      <main>
        <Hero />
        
        <Suspense fallback={<LoadingFallback />}>
          <HowItWorks />
        </Suspense>
        
        <Suspense fallback={<LoadingFallback />}>
          <TripleAdvantage />
        </Suspense>
        
        <Suspense fallback={<LoadingFallback />}>
          <CustomerSuccessStories />
        </Suspense>
        
        <Suspense fallback={<LoadingFallback />}>
          <VariantTable />
        </Suspense>
        
        <Suspense fallback={<LoadingFallback />}>
          <ATSIntegrations />
        </Suspense>
        
        <Suspense fallback={<LoadingFallback />}>
          <FAQ tags={["general", "home"]} />
        </Suspense>
      </main>
      
      <Suspense fallback={<LoadingFallback />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
